import React, { PropsWithChildren } from 'react';
import { MappedComponentProperties } from '@adobe/aem-react-editable-components';
import { Icon as Icons } from '../../types/shared/Icon';
import { transformHTMLParserLinks } from '../../utils/rewriteLink/rewriteLink';
import './HelpCenterArticleContainer.css';
import { useWidthContext } from '../../providers/Width';
import { ButtonWrapper } from '../LinkWrapper/LinkWrapper';
import { wrapTrademarkSuperscript } from '../../utils/cob-utils';
import { Title, Section, Container, Icon, Row, Col } from '../../importCL';

interface IHelpCenterArticleContentFragment {
    titleText?: string;
    icon?: Icons; //single icon
    detailDescription?: string;
}

interface IHelpCenterNavContentFragment {
    previousText: string;
    previousLink: string;
    nextText: string;
    nextLink: string;
}

interface IHelpCenterArticleContainerProps extends MappedComponentProperties {
    path?: string;
    prevLink?: string;
    prevText?: string;
    nextLink?: string;
    nextText?: string;
    contentFragment?: IHelpCenterArticleContentFragment;
    helpCenterNavContentFragment?: IHelpCenterNavContentFragment;
}

export const HelpCenterArticleContainerEditConfig = {
    emptyLabel: 'Help Center Article Container',
    isEmpty: (props: PropsWithChildren<IHelpCenterArticleContainerProps>) => {
        return !props || !props.path;
    },
};

const HelpCenterArticleContainer = (props: PropsWithChildren<IHelpCenterArticleContainerProps>): JSX.Element => {
    const cfm = props.contentFragment;
    const navCF = props.helpCenterNavContentFragment;
    const { width } = useWidthContext();
    const prevText = props.prevText ?? (navCF?.previousText ?? '');
    const previousLink = props.prevLink ?? (navCF?.previousLink ?? '');
    const nextText = props.nextText ?? (navCF?.nextText ?? '');
    const nextLink = props.nextLink ?? (navCF?.nextLink ?? '');

    return (
        <div id='help-center-article-container' className='HelpCenterArticleContent my-0 mx-auto max-w-6xl pt-4 px-3'>
            <Section className='!py-5' containerClass='HelpCenterArticleContent__container !p-0'>
                <Row className='justify-start items-center min-w-0 mx-0'>
                    <Col className='!p-0 m-0 min-w-0 !flex-none'>
                        {cfm?.icon && (
                            <Icon loading='lazy' src={cfm?.icon.iconPath} />
                        )}
                    </Col>
                    <Col className='!px-0'>
                        <Title className='mb-0 ml-4 p-0' tag='h4'>
                            {transformHTMLParserLinks(wrapTrademarkSuperscript(cfm?.titleText))}
                        </Title>
                    </Col>
                </Row>
            </Section>
            <hr tabIndex={-1} aria-hidden='true' className='my-4 mx-0' />
            <Container className='HelpCenterArticleContent__container !py-8 !px-4'>
                {cfm?.detailDescription && transformHTMLParserLinks(cfm?.detailDescription)}
            </Container>
            <Section className='HelpCenterArticleContent__links !p-0 mb-12' containerClass='HelpCenterArticleContent__container2 !px-4'>
                <Row className='w-full'>
                    <Col className='!p-0'>
                        <ButtonWrapper className='!p-0 my-4 mx-2 !leading-[1.125rem] border-none' href={previousLink} variant='link' size='medium' icon={width > 576 ? 'back' : ''}>
                            {transformHTMLParserLinks(wrapTrademarkSuperscript(prevText))}
                        </ButtonWrapper>
                    </Col>
                    <Col className='!p-0'>
                        <ButtonWrapper className='!p-0 my-4 mx-2 !leading-[1.125rem] border-none' href={nextLink} variant='link' size='medium' icon={width > 576 ? 'chevron' : ''}>
                            {transformHTMLParserLinks(wrapTrademarkSuperscript(nextText))}
                        </ButtonWrapper>
                    </Col>
                </Row>
            </Section>
        </div>
    );
};

export default HelpCenterArticleContainer;
